import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, Pin, PinOff, Search, Trophy, X } from "lucide-react";
import type { MapFeatureValue, RegionTimeSeries, TimeSeriesPoint } from "@/features/explorer/api/contracts";
import { explorerQueryKeys, ANALYTICS_STALE_TIME_MS, ANALYTICS_GC_TIME_MS } from "@/features/explorer/api/query-keys";
import { getRegionTimeSeries } from "@/features/explorer/services/national-data-api";
import type { ExplorerSelection } from "@/features/explorer/types";
import { useChartTheme } from "@/components/charts/chart-theme";
import { useAutoMinimize } from "@/hooks/use-auto-minimize";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { CollapsibleRegion } from "@/components/ui/collapsible-region";
import { Sparkline } from "./Sparkline";

const DESKTOP_COUNT = 5;
const MOBILE_COUNT = 4;
const SEARCH_LIMIT = 8;
const EMPTY_PINS: readonly string[] = [];

type RankingMode = "top" | "bottom" | "pinned";

export type FocusedRegion = {
  geographyCode: string;
  name: string;
  value: number | null;
  qualityFlag?: string | null;
};

type RankingStripProps = {
  selection: ExplorerSelection;
  values: readonly MapFeatureValue[];
  nameById: ReadonlyMap<string, string>;
  formatValue: (value: number | null) => string;
  pinnedCodes?: readonly string[];
  onTogglePin?: (code: string) => void;
  onFocusRegion?: (region: FocusedRegion) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  disableAutoMinimize?: boolean;
  className?: string;
};

function firstNonNull(points: readonly TimeSeriesPoint[]): number | null {
  for (const point of points) {
    if (point.value !== null && Number.isFinite(point.value)) return point.value;
  }
  return null;
}

/**
 * Bottom ranking strip for the single-metric map. Ranks areas by their current
 * value (top / bottom) and draws a per-area trend sparkline from the region
 * time-series endpoint. On desktop it shows five cards in a row; on mobile it
 * shows four in a 2x2 grid. Collapses to a slim drawer bar via the chevron, or
 * by clicking the header bar / collapsed strip.
 *
 * Beyond ranking it doubles as the pin point for the explorer: each card can be
 * pinned, a "Pinned" tab focuses your shortlist (order = pin order), and an
 * inline search lets you find any area by name to pin or open in the comparison
 * flyout. Pins are persisted by the parent in the URL.
 */
export function RankingStrip({
  selection,
  values,
  nameById,
  formatValue,
  pinnedCodes = EMPTY_PINS,
  onTogglePin,
  onFocusRegion,
  collapsed: controlledCollapsed,
  onToggleCollapse,
  disableAutoMinimize = false,
  className,
}: RankingStripProps) {
  const { tokens } = useChartTheme();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isLg = useMediaQuery("(min-width: 1024px) and (max-width: 1279px)");
  const [mode, setMode] = useState<RankingMode>("top");
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    if (globalThis.window !== undefined) {
      return globalThis.innerWidth >= 1024 && globalThis.innerWidth < 1280;
    }
    return false;
  });

  const collapsed = controlledCollapsed ?? internalCollapsed;
  const setCollapsed = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      const nextValue = typeof next === "function" ? next(collapsed) : next;
      setInternalCollapsed(nextValue);
      onToggleCollapse?.();
    },
    [collapsed, onToggleCollapse],
  );

  const containerRef = useAutoMinimize({
    isExpanded: !collapsed,
    disabled: disableAutoMinimize,
    onMinimize: () => {
      setCollapsed(true);
    },
    delayMs: 5000,
  });

  // Search state (search button / dropdown)
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const focusSearchInput = () => {
    // Focus the input on the next frame once the search block has mounted.
    globalThis.requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const rankingCount = isMobile || isLg ? MOBILE_COUNT : DESKTOP_COUNT;

  // Fall back to "top" when the pinned tab is empty so the strip never renders
  // a bare header.
  const activeMode = mode === "pinned" && pinnedCodes.length === 0 ? "top" : mode;

  const { ranked, totalCount } = useMemo(() => {
    const valid = values.filter((value) => value.value !== null && !Number.isNaN(value.value));
    const sorted = [...valid].toSorted((a, b) => (b.value ?? 0) - (a.value ?? 0));
    const slice = mode === "bottom" ? sorted.slice(-rankingCount).toReversed() : sorted.slice(0, rankingCount);
    return { ranked: slice, totalCount: valid.length };
  }, [values, mode, rankingCount]);

  const pinnedRanked = useMemo(() => {
    const byCode = new Map(values.map((value) => [value.geographyCode, value]));
    return pinnedCodes.map((code) => byCode.get(code)).filter((value): value is MapFeatureValue => Boolean(value));
  }, [values, pinnedCodes]);

  const display = activeMode === "pinned" ? pinnedRanked : ranked;
  const codes = useMemo(() => display.map((item) => item.geographyCode), [display]);

  const { data, isPending } = useQuery<RegionTimeSeries>({
    queryKey: explorerQueryKeys.regionTimeSeries({
      metricId: selection.metricId,
      geographyLevel: selection.geographyLevel,
      statisticId: selection.statisticId,
      geographyCodes: codes,
    }),
    queryFn: () =>
      getRegionTimeSeries({
        metricId: selection.metricId,
        geographyLevel: selection.geographyLevel,
        statisticId: selection.statisticId,
        geographyCodes: codes,
      }),
    enabled: codes.length > 0,
    staleTime: ANALYTICS_STALE_TIME_MS,
    gcTime: ANALYTICS_GC_TIME_MS,
    refetchOnWindowFocus: false,
  });

  const seriesByCode = useMemo(() => {
    const map = new Map<string, TimeSeriesPoint[]>();
    for (const series of data?.series ?? []) map.set(series.geographyCode, series.points);
    return map;
  }, [data]);

  const valueByCode = useMemo(() => {
    const map = new Map<string, MapFeatureValue>();
    for (const value of values) map.set(value.geographyCode, value);
    return map;
  }, [values]);

  const searchResults = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || nameById.size === 0) return [];
    const matches: [string, string][] = [];
    for (const [code, name] of nameById.entries()) {
      if (name.toLowerCase().includes(term)) matches.push([code, name]);
      if (matches.length >= SEARCH_LIMIT) break;
    }
    return matches;
  }, [query, nameById]);

  const handleToggle = () => {
    setCollapsed((current: boolean) => !current);
  };

  const handleFocus = (code: string, name: string) => {
    const obs = valueByCode.get(code);
    onFocusRegion?.({
      geographyCode: code,
      name,
      value: obs?.value ?? null,
      qualityFlag: obs?.qualityFlag ?? null,
    });
    setSearchOpen(false);
    setQuery("");
  };

  const isPinned = (code: string) => pinnedCodes.includes(code);

  const positionOf = (index: number) => (mode === "bottom" ? totalCount - ranked.length + index + 1 : index + 1);

  const toggleButtonClass =
    "flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary";

  const renderCard = (item: MapFeatureValue, index: number, kind: "ranked" | "pinned") => {
    const points = seriesByCode.get(item.geographyCode) ?? [];
    const seriesValues = points.map((point) => point.value);
    const onset = firstNonNull(points);
    const last = points.at(-1)?.value ?? null;
    const up = onset !== null && last !== null && last >= onset;
    const name = nameById.get(item.geographyCode) ?? item.geographyCode;
    const pinned = isPinned(item.geographyCode);

    return (
      <div
        key={item.geographyCode}
        className='relative min-w-0 flex-1 rounded-md border border-border bg-muted/30 p-2'
        title={name}>
        <button
          type='button'
          onClick={() => onTogglePin?.(item.geographyCode)}
          aria-pressed={pinned}
          aria-label={pinned ? `Unpin ${name}` : `Pin ${name}`}
          className={cn("absolute top-1.5 right-1.5", toggleButtonClass, pinned && "text-primary hover:text-primary")}>
          {pinned ? <Pin className='size-3.5' /> : <PinOff className='size-3.5 opacity-60' />}
        </button>

        <span className='rounded-xs bg-primary/10 px-1.5 py-0.5 font-mono text-xs font-bold text-primary'>
          #{kind === "pinned" ? index + 1 : positionOf(index)}
        </span>
        <p className='mt-1.5 pr-5 truncate text-sm font-bold leading-tight text-card-foreground'>{name}</p>
        <div className='mt-0.5 flex items-center justify-between gap-1'>
          <p className='truncate font-mono text-sm font-bold text-primary'>{formatValue(item.value)}</p>
          {onset !== null && last !== null && (
            <span
              className={cn(
                "flex shrink-0 items-center gap-0.5 font-mono text-xs font-bold",
                up ? "text-success" : "text-destructive",
              )}>
              {up ? <ArrowUp className='size-3' /> : <ArrowDown className='size-3' />}
            </span>
          )}
        </div>
        <div className='mt-1.5 h-[26px]'>
          {isPending || seriesValues.length === 0 ? (
            <div className='size-full animate-pulse rounded bg-muted/40' aria-hidden='true' />
          ) : (
            <Sparkline points={seriesValues} color={up ? tokens.success : tokens.destructive} />
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "pointer-events-auto flex flex-col w-full rounded-lg border border-border bg-card/95 font-sans text-card-foreground backdrop-blur-2xl transition-shadow duration-300",
        collapsed ? "shadow-lg" : "shadow-2xl",
        className,
      )}>
      {collapsed ? (
        <button
          type='button'
          onClick={handleToggle}
          aria-expanded={false}
          aria-controls='ranking-strip-body'
          className='flex w-full cursor-pointer items-center justify-between rounded-lg px-4 py-3 text-base font-bold text-card-foreground transition-colors hover:bg-muted'>
          <div className='flex items-center gap-2.5'>
            <Trophy className='size-4 text-primary' />
            <span>Regional Ranking</span>
          </div>
          <ChevronDown className='size-4 text-muted-foreground' />
        </button>
      ) : (
        <div className='shrink-0 px-4 pt-4 mb-3 flex w-full items-center justify-between border-b border-border pb-3 text-left'>
          <button
            type='button'
            onClick={handleToggle}
            className='flex cursor-pointer items-center gap-2 transition-opacity hover:opacity-80'
            aria-label='Minimize ranking strip'
            aria-expanded
            aria-controls='ranking-strip-body'>
            <Trophy className='size-4 text-primary' />
            <h2 className='text-base font-bold tracking-wider text-card-foreground uppercase'>Regional Ranking</h2>
          </button>

          {/* On XL & 2XL: Inline controls & Chevron */}
          {!isLg && (
            <div className='flex items-center gap-1.5'>
              <div className='flex gap-0.5 rounded-md border border-border bg-muted/40 p-0.5'>
                <button
                  type='button'
                  onClick={() => {
                    setMode("top");
                  }}
                  className={cn(
                    "cursor-pointer rounded-sm px-2 py-0.5 text-xs font-semibold transition-colors",
                    activeMode === "top"
                      ? "bg-card text-primary shadow-xs font-bold"
                      : "text-muted-foreground hover:text-foreground",
                  )}>
                  Top
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setMode("bottom");
                  }}
                  className={cn(
                    "cursor-pointer rounded-sm px-2 py-0.5 text-xs font-semibold transition-colors",
                    activeMode === "bottom"
                      ? "bg-card text-primary shadow-xs font-bold"
                      : "text-muted-foreground hover:text-foreground",
                  )}>
                  Bottom
                </button>
                {pinnedCodes.length > 0 && (
                  <button
                    type='button'
                    onClick={() => {
                      setMode("pinned");
                    }}
                    className={cn(
                      "cursor-pointer rounded-sm px-2 py-0.5 text-xs font-semibold transition-colors",
                      activeMode === "pinned"
                        ? "bg-card text-primary shadow-xs font-bold"
                        : "text-muted-foreground hover:text-foreground",
                    )}>
                    <span className='inline-flex items-center gap-1'>
                      <Pin className='size-3' />
                      {pinnedCodes.length}
                    </span>
                  </button>
                )}
              </div>

              <button
                type='button'
                onClick={() => {
                  setSearchOpen((current) => {
                    const next = !current;
                    setQuery("");
                    if (next) focusSearchInput();
                    return next;
                  });
                }}
                title='Search areas'
                aria-label='Search areas'
                aria-expanded={searchOpen}
                className='flex h-6 cursor-pointer items-center justify-center rounded-md border border-border bg-muted/40 px-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'>
                {searchOpen ? <X className='size-3.5' /> : <Search className='size-3.5' />}
              </button>

              <button
                type='button'
                onClick={handleToggle}
                title='Collapse ranking'
                aria-label='Collapse ranking'
                className='flex h-6 cursor-pointer items-center justify-center rounded-md border border-border bg-muted/40 px-2 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'>
                <ChevronUp className='size-3.5' />
              </button>
            </div>
          )}

          {/* On LG: Only Chevron button in top header row */}
          {isLg && (
            <button
              type='button'
              onClick={handleToggle}
              aria-label='Minimize ranking strip'
              aria-expanded
              aria-controls='ranking-strip-body'
              className='rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-card-foreground'>
              <ChevronUp className='size-4' />
            </button>
          )}
        </div>
      )}

      <CollapsibleRegion open={!collapsed} id='ranking-strip-body'>
        <div className='px-4 pb-4'>
          {/* On LG: Toolbar Controls Placed Below Header Text */}
          {isLg && (
            <div className='shrink-0 mb-3 flex items-center justify-between gap-2 border-b border-border pb-3'>
              <div className='flex gap-0.5 rounded-md border border-border bg-muted/60 p-1 text-xs font-semibold'>
                <button
                  type='button'
                  onClick={() => {
                    setMode("top");
                  }}
                  className={cn(
                    "cursor-pointer rounded-xs px-2.5 py-1 text-xs font-bold transition-all",
                    activeMode === "top"
                      ? "bg-primary text-primary-foreground shadow-xs"
                      : "text-muted-foreground hover:text-card-foreground",
                  )}>
                  Top
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setMode("bottom");
                  }}
                  className={cn(
                    "cursor-pointer rounded-xs px-2.5 py-1 text-xs font-bold transition-all",
                    activeMode === "bottom"
                      ? "bg-primary text-primary-foreground shadow-xs"
                      : "text-muted-foreground hover:text-card-foreground",
                  )}>
                  Bottom
                </button>
                {pinnedCodes.length > 0 && (
                  <button
                    type='button'
                    onClick={() => {
                      setMode("pinned");
                    }}
                    className={cn(
                      "cursor-pointer rounded-xs px-2.5 py-1 text-xs font-bold transition-all",
                      activeMode === "pinned"
                        ? "bg-primary text-primary-foreground shadow-xs"
                        : "text-muted-foreground hover:text-card-foreground",
                    )}>
                    <span className='inline-flex items-center gap-1'>
                      <Pin className='size-3' />
                      {pinnedCodes.length}
                    </span>
                  </button>
                )}
              </div>

              <button
                type='button'
                onClick={() => {
                  setSearchOpen((current) => {
                    const next = !current;
                    setQuery("");
                    if (next) focusSearchInput();
                    return next;
                  });
                }}
                title='Search areas'
                aria-label='Search areas'
                aria-expanded={searchOpen}
                className='flex h-7 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-border bg-muted/40 px-2.5 text-xs font-bold text-muted-foreground transition-colors hover:bg-muted hover:text-card-foreground'>
                {searchOpen ? <X className='size-3.5' /> : <Search className='size-3.5' />}
                <span>Search</span>
              </button>
            </div>
          )}

          {searchOpen && (
            <div className='relative mt-2'>
              <div className='relative'>
                <Search className='pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground' />
                <input
                  ref={searchInputRef}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                  }}
                  placeholder='Search areas by name…'
                  aria-label='Search areas by name'
                  className='w-full rounded-md border border-border bg-muted/40 py-1.5 pl-8 pr-3 text-sm text-card-foreground outline-none placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary'
                />
              </div>

              {(query.trim() || searchResults.length > 0) && (
                <ul className='absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-card p-1 shadow-2xl'>
                  {searchResults.length === 0 ? (
                    <li className='px-3 py-2 text-sm text-muted-foreground'>No matching areas</li>
                  ) : (
                    searchResults.map(([code, name]) => {
                      const pinned = isPinned(code);
                      return (
                        <li key={code} className='flex items-center gap-1'>
                          <button
                            type='button'
                            onClick={() => {
                              handleFocus(code, name);
                            }}
                            className='min-w-0 flex-1 cursor-pointer rounded-sm px-2.5 py-1.5 text-left text-sm text-card-foreground transition-colors hover:bg-muted/60 hover:text-primary'>
                            <span className='block truncate font-medium'>{name}</span>
                            <span className='block truncate font-mono text-xs text-muted-foreground'>
                              {nameById.get(code) === code ? code : ""}
                              {valueByCode.has(code) ? ` • ${formatValue(valueByCode.get(code)?.value ?? null)}` : ""}
                            </span>
                          </button>
                          {onTogglePin && (
                            <button
                              type='button'
                              onClick={() => {
                                onTogglePin(code);
                              }}
                              aria-label={pinned ? `Unpin ${name}` : `Pin ${name}`}
                              aria-pressed={pinned}
                              className={cn(
                                toggleButtonClass,
                                "mr-1 shrink-0",
                                pinned && "text-primary hover:text-primary",
                              )}>
                              {pinned ? <Pin className='size-3.5' /> : <PinOff className='size-3.5 opacity-60' />}
                            </button>
                          )}
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>
          )}

          {activeMode === "pinned" && pinnedRanked.length === 0 && (
            <p className='mt-2.5 px-1 text-sm text-muted-foreground'>No pinned areas have data for this metric.</p>
          )}

          {display.length > 0 && (
            <div
              id='ranking-strip-cards'
              className={cn("mt-2.5 gap-2", isMobile || isLg ? "grid grid-cols-2" : "flex")}>
              {display.map((item, index) => renderCard(item, index, activeMode === "pinned" ? "pinned" : "ranked"))}
            </div>
          )}
        </div>
      </CollapsibleRegion>
    </div>
  );
}
